import { useEffect, useRef, useState } from 'react';
import { APP_URL, REGISTER_URL, SIGN_IN_URL } from '../../constants';
import { useLanguage } from '../../i18n/LanguageContext';
import { asset } from '../../lib/asset';
import { advanceTickerCursor, peekTickerItems } from '../../lib/generateTickerItems';
import LanguageSelect from '../LanguageSelect';

const TICKER_WINDOW = 28;

function ChevronDown() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
            <path d="M6 9l6 6 6-6" />
        </svg>
    );
}

function Header() {
    const { t, language } = useLanguage();
    const [tickerItems, setTickerItems] = useState(() => peekTickerItems('en', TICKER_WINDOW));

    useEffect(() => {
        setTickerItems(peekTickerItems(language, TICKER_WINDOW));
    }, [language]);

    useEffect(() => {
        let advanced = false;
        const advanceOnce = () => {
            if (advanced) return;
            advanced = true;
            advanceTickerCursor(TICKER_WINDOW);
        };
        window.addEventListener('pagehide', advanceOnce);
        return () => {
            window.removeEventListener('pagehide', advanceOnce);
            advanceOnce();
        };
    }, []);
    
    const [mobileOpen, setMobileOpen] = useState(false);
    const [shopOpen, setShopOpen] = useState(false);
    const [moreOpen, setMoreOpen] = useState(false);
    const shopRef = useRef(null);
    const moreRef = useRef(null);

    useEffect(() => {
        const onDocClick = (e) => {
            if (!shopRef.current?.contains(e.target)) setShopOpen(false);
            if (!moreRef.current?.contains(e.target)) setMoreOpen(false);
        };
        const onKey = (e) => {
            if (e.key === 'Escape') {
                setShopOpen(false);
                setMoreOpen(false);
            }
        };
        document.addEventListener('click', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('click', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, []);

    const closeMobile = () => setMobileOpen(false);

    return (
        <header className="top">
            <div className="ticker" aria-label={t.tickerLabel}>
                <div className="ticker-track">
                    {[0, 1].map((pass) =>
                        tickerItems.map((item, i) => (
                            <span
                                className="ticker-item"
                                key={`${pass}-${item.id}-${i}`}
                                aria-hidden={pass === 1 ? true : undefined}
                            >
                                <i className="pip" />
                                <b>{item.name}</b> {item.action}{' '}
                                <span className="mono">{item.amount}</span>
                            </span>
                        ))
                    )}
                </div>
            </div>
            <div className="wrap">
                <a className="brand" href="#top" aria-label={t.brandHome}>
                    <img src={asset('apc-plus-logo.png')} alt="APC Plus" width="900" height="273" />
                </a>
                <nav className="nav" aria-label="Main">
                    <a href="#red-packets">{t.navPackets}</a>
                    <div className="dd" ref={shopRef}>
                        <button
                            type="button"
                            className="dd-btn"
                            aria-expanded={shopOpen}
                            aria-controls="ddShop"
                            onClick={(e) => {
                                e.stopPropagation();
                                setMoreOpen(false);
                                setShopOpen((v) => !v);
                            }}
                        >
                            {t.navShopTravel} <ChevronDown />
                        </button>
                        <div className="dd-panel" id="ddShop" hidden={!shopOpen}>
                            <a href={APP_URL} onClick={() => setShopOpen(false)}>
                                <span className="ico" style={{ width: 38, height: 38 }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="2" y="6" width="20" height="13" rx="2" />
                                        <path d="M2 10h20M7 15h4" />
                                    </svg>
                                </span>
                                <span>
                                    <b>{t.ddGiftCards}</b>
                                    <span>{t.ddGiftCardsDesc}</span>
                                </span>
                            </a>
                            <a href={APP_URL} onClick={() => setShopOpen(false)}>
                                <span className="ico" style={{ width: 38, height: 38 }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M7 2h7l5 5v13a2 2 0 01-2 2H7a2 2 0 01-2-2V4a2 2 0 012-2z" />
                                        <rect x="8.5" y="11" width="7" height="7" rx="1" />
                                    </svg>
                                </span>
                                <span>
                                    <b>{t.ddEsims}</b>
                                    <span>{t.ddEsimsDesc}</span>
                                </span>
                            </a>
                            <a href={APP_URL} onClick={() => setShopOpen(false)}>
                                <span className="ico" style={{ width: 38, height: 38 }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M2 16l20-6-3-3-6 2-6-5-2 1 4 6-4 1-2-2-2 1z" />
                                    </svg>
                                </span>
                                <span>
                                    <b>{t.ddTravel}</b>
                                    <span>{t.ddTravelDesc}</span>
                                </span>
                            </a>
                            <a href="#rewards" onClick={() => setShopOpen(false)}>
                                <span className="ico" style={{ width: 38, height: 38 }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M12 2v20M17 6H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                                    </svg>
                                </span>
                                <span>
                                    <b>{t.ddCashback}</b>
                                    <span>{t.ddCashbackDesc}</span>
                                </span>
                            </a>
                        </div>
                    </div>
                    <a href="#rewards">{t.navRewards}</a>
                    <a href="#trust">{t.navSafety}</a>
                    <div className="dd" ref={moreRef}>
                        <button
                            type="button"
                            className="dd-btn"
                            aria-expanded={moreOpen}
                            aria-controls="ddPanel"
                            onClick={(e) => {
                                e.stopPropagation();
                                setShopOpen(false);
                                setMoreOpen((v) => !v);
                            }}
                        >
                            {t.navMore} <ChevronDown />
                        </button>
                        <div className="dd-panel" id="ddPanel" hidden={!moreOpen}>
                            <a href={APP_URL} onClick={() => setMoreOpen(false)}>
                                <span className="ico" style={{ width: 38, height: 38 }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="9" cy="8" r="3" />
                                        <circle cx="17" cy="9" r="2.5" />
                                        <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6M15 14.5c3 0 6 1.8 6 5" />
                                    </svg>
                                </span>
                                <span>
                                    <b>{t.ddCircles}</b>
                                    <span>{t.ddCirclesDesc}</span>
                                </span>
                            </a>
                            <a href="#roadmap" onClick={() => setMoreOpen(false)}>
                                <span className="ico" style={{ width: 38, height: 38 }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M4 19V9m5 10V5m6 14v-7m5 7V3" />
                                    </svg>
                                </span>
                                <span>
                                    <b>{t.ddRwaStocks}</b>
                                    <span>{t.ddRwaStocksDesc}</span>
                                </span>
                            </a>
                            <a href="#trust" onClick={() => setMoreOpen(false)}>
                                <span className="ico" style={{ width: 38, height: 38 }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6z" />
                                        <path d="M8 12l3 3 5-6" />
                                    </svg>
                                </span>
                                <span>
                                    <b>{t.ddSecurityReserves}</b>
                                    <span>{t.ddSecurityReservesDesc}</span>
                                </span>
                            </a>
                            <a href="#roadmap" onClick={() => setMoreOpen(false)}>
                                <span className="ico" style={{ width: 38, height: 38 }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2z" />
                                        <path d="M9 4v14M15 6v14" />
                                    </svg>
                                </span>
                                <span>
                                    <b>{t.navRoadmap}</b>
                                    <span>{t.ddRoadmapDesc}</span>
                                </span>
                            </a>
                        </div>
                    </div>
                </nav>
                <div className="top-cta">
                    <LanguageSelect />
                    <a className="btn btn-ghost btn-sm" href={SIGN_IN_URL}>
                        {t.signIn}
                    </a>
                    <a className="btn btn-primary btn-sm" href={REGISTER_URL}>
                        {t.getStarted}
                    </a>
                </div>
                <button
                    type="button"
                    className="menu-btn"
                    aria-expanded={mobileOpen}
                    aria-controls="mobileNav"
                    aria-label={t.openMenu}
                    onClick={() => setMobileOpen((v) => !v)}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M4 7h16M4 12h16M4 17h16" />
                    </svg>
                </button>
            </div>
            <nav className={`mobile-nav${mobileOpen ? ' open' : ''}`} id="mobileNav" aria-label="Mobile">
                <a href="#red-packets" onClick={closeMobile}>
                    {t.navPackets}
                </a>
                <a href="#everyday" onClick={closeMobile}>
                    {t.navShopTravel}
                </a>
                <a href="#rewards" onClick={closeMobile}>
                    {t.navRewards}
                </a>
                <a href="#trust" onClick={closeMobile}>
                    {t.navSafety}
                </a>
                <a href="#roadmap" onClick={closeMobile}>
                    {t.navRoadmap}
                </a>
                <a href={SIGN_IN_URL} onClick={closeMobile}>
                    {t.signIn}
                </a>
            </nav>
        </header>
    );
}

export default Header;
